// Growem — pure undo/redo history over per-day snapshots (audited design, 2026-07-31).
//
// Model: every user-facing day mutation may push an Entry carrying the PREVIOUS DayState plus a
// human label ("serie completada (S2 de Remo en polea baja)"). Undo restores that snapshot exactly
// (full-day replace: no partial inverses that can diverge) and moves the pre-undo state to the redo
// stack. Audited rules baked in:
//  - COALESCING applies ONLY to continuous-input actions (typing weight/reps/RIR, notes, target
//    override): same (day, actionKey) within a 2 s sliding idle window updates the label/timestamp
//    but keeps the ORIGINAL prev (state before the first keystroke). Toggles, counters (water/
//    protein/kcal/creatine), add/remove/reorder/variant NEVER coalesce: opposite taps must stay
//    separate entries or the popup would lie (e.g. 3 water taps are 3 entries, each "+250 ml").
//  - Every push clears the redo stack, INCLUDING the coalesced branch (otherwise a coalesced edit
//    after an undo leaves a stale redo that silently wipes the fresh edit).
//  - undo/redo SEAL the entry left on top of the undo stack so later keystrokes can never coalesce
//    across an undo/redo boundary.
//  - Cap 30 entries (oldest evicted). Session-only: the store clears stacks on load/sign-out.

import type { DayState } from '../types'

export interface HistoryEntry {
  day: string
  label: string        // exactly what the confirmation popup shows after "deshacer:"
  actionKey: string    // identity for coalescing, e.g. "field:4-0-1:w"
  prev: DayState       // full-day snapshot BEFORE the action
  at: number           // last update (ms) for the coalescing window
  sealed?: boolean     // set on undo/redo boundaries: never coalesce into this entry
}

export interface HistoryStacks { undo: HistoryEntry[]; redo: HistoryEntry[] }

export const HISTORY_CAP = 30
const COALESCE_MS = 2000
/** Only continuous-input actions may coalesce (prefix match). */
const COALESCE_PREFIXES = ['field:', 'note:', 'target:']

export function emptyStacks(): HistoryStacks { return { undo: [], redo: [] } }

/** Push a new entry (or coalesce into the top one when allowed). Always clears redo. */
export function pushEntry(stacks: HistoryStacks, entry: Omit<HistoryEntry, 'at'>, nowMs: number): HistoryStacks {
  const coalescable = COALESCE_PREFIXES.some((p) => entry.actionKey.startsWith(p))
  const top = stacks.undo[stacks.undo.length - 1]
  if (coalescable && top && !top.sealed && top.day === entry.day && top.actionKey === entry.actionKey &&
      nowMs - top.at < COALESCE_MS) {
    // Same continuous edit: keep the ORIGINAL prev, refresh label/timestamp. Redo still clears.
    const undo = [...stacks.undo.slice(0, -1), { ...top, label: entry.label, at: nowMs }]
    return { undo, redo: [] }
  }
  const undo = [...stacks.undo, { ...entry, at: nowMs }]
  if (undo.length > HISTORY_CAP) undo.shift()
  return { undo, redo: [] }
}

/** Undo the top entry. Returns the restore target + new stacks (current state goes to redo). */
export function undoStep(stacks: HistoryStacks, current: DayState): { day: string; restore: DayState; label: string; stacks: HistoryStacks } | null {
  const top = stacks.undo[stacks.undo.length - 1]
  if (!top) return null
  const undo = stacks.undo.slice(0, -1)
  // Seal the new top: later keystrokes must never coalesce across this boundary.
  if (undo.length) undo[undo.length - 1] = { ...undo[undo.length - 1], sealed: true }
  const redo = [...stacks.redo, { ...top, prev: current, sealed: true }]
  return { day: top.day, restore: top.prev, label: top.label, stacks: { undo, redo } }
}

/** Redo the top redo entry. Current state goes back to the undo stack (sealed). */
export function redoStep(stacks: HistoryStacks, current: DayState): { day: string; restore: DayState; label: string; stacks: HistoryStacks } | null {
  const top = stacks.redo[stacks.redo.length - 1]
  if (!top) return null
  const redo = stacks.redo.slice(0, -1)
  const undo = [...stacks.undo, { ...top, prev: current, sealed: true }]
  return { day: top.day, restore: top.prev, label: top.label, stacks: { undo, redo } }
}
